import {
  IBackendPosProvider,
  IBackendPosProviderSquare,
  IBackendDeliveryModuleConfig,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- grace - before eslint enforced
  IBackendPosProviderStream,
} from "../../types/backend";
import { IPosProvider } from "../../types/pos";
import { produce } from "immer";
import {
  convertMenuConfigToBackend,
  convertMenuConfigToFrontend,
  convertPrepTimeToBackend,
  convertPrepTimeToFrontend,
} from "./menu-converter";

export function convertPosProviderToFrontend(
  backendPosProvider: IBackendPosProvider,
): IPosProvider | null {
  if (backendPosProvider == null) {
    return null;
  }

  return produce(backendPosProvider as unknown as IPosProvider, (draft) => {
    if (draft?.orderingModuleConfig?.deliveryModuleConfig) {
      // Convert waiveDeliveryMinimum
      draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum,
        );

      // convert delivery fee
      draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee =
        convertCurrencyToFrontend(
          draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee,
        ) || 0;

      // Convert coordinates from object format
      const coordinates = (
        draft.orderingModuleConfig
          .deliveryModuleConfig as IBackendDeliveryModuleConfig
      ).coordinates;
      if (coordinates) {
        draft.orderingModuleConfig.deliveryModuleConfig.latitude =
          coordinates.latitude;
        draft.orderingModuleConfig.deliveryModuleConfig.longitude =
          coordinates.longitude;
        delete (
          draft.orderingModuleConfig
            .deliveryModuleConfig as IBackendDeliveryModuleConfig
        ).coordinates;
      }
    }

    if (draft?.orderingModuleConfig) {
      // Convert prepTime
      draft.orderingModuleConfig.prepTime = convertPrepTimeToFrontend(
        backendPosProvider.orderingModuleConfig.prepTime,
      );

      // Convert min lead time
      if (backendPosProvider.orderingModuleConfig.minLeadTime) {
        draft.orderingModuleConfig.minLeadTime = convertPrepTimeToFrontend(
          backendPosProvider.orderingModuleConfig.minLeadTime,
        );
      }

      // Convert min total threshold
      if (backendPosProvider.orderingModuleConfig.minTotalThreshold) {
        draft.orderingModuleConfig.minTotalThreshold =
          convertCurrencyToFrontend(
            backendPosProvider.orderingModuleConfig.minTotalThreshold,
          ) || 0;
      }

      // Convert max total threshold
      if (backendPosProvider.orderingModuleConfig.maxTotalThreshold) {
        draft.orderingModuleConfig.maxTotalThreshold =
          convertCurrencyToFrontend(
            backendPosProvider.orderingModuleConfig.maxTotalThreshold,
          ) || 0;
      }

      // Convert taxRate if it exists
      if (
        "taxRate" in backendPosProvider.orderingModuleConfig &&
        backendPosProvider.orderingModuleConfig.taxRate !== undefined
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- grace - before eslint enforced
        (draft.orderingModuleConfig as any).taxRate = convertTaxRateToFrontend(
          backendPosProvider.orderingModuleConfig.taxRate as number,
        );
      }
    }

    // Apply menu config conversions
    if (draft?.menuModuleConfig && backendPosProvider.menuModuleConfig) {
      Object.assign(
        draft.menuModuleConfig,
        convertMenuConfigToFrontend(backendPosProvider.menuModuleConfig),
      );
    }
  });
}

export function convertTaxRateToFrontend(taxRate: number): number {
  return taxRate * 100.0;
}

export function convertTaxRateToBackend(taxRate: number): number {
  return taxRate / 100.0;
}

function convertCurrencyToFrontend(
  currencyCents: number | null | undefined,
): number | null | undefined {
  if (currencyCents === undefined) {
    return undefined;
  }
  if (currencyCents === null) {
    return null;
  }
  // to dollars
  return parseFloat((currencyCents / 100.0).toFixed(2));
}

function convertCurrencyToBackend(
  currencyDollars: number | null | undefined,
): number | null | undefined {
  if (currencyDollars === undefined) {
    return undefined;
  }
  if (currencyDollars === null) {
    return null;
  }
  // to cents
  return Math.round(currencyDollars * 100);
}

export function convertPosProviderToBackend(
  posProvider: IPosProvider | null,
): IBackendPosProvider | null {
  if (!posProvider) return null;

  return produce(posProvider as unknown as IBackendPosProvider, (draft) => {
    if (draft?.orderingModuleConfig?.deliveryModuleConfig) {
      // Convert waiveDeliveryMinimum from dollars to cents
      draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.deliveryModuleConfig.waiveDeliveryMinimum,
        );

      // Convert delivery fee from dollars to cents
      draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee =
        convertCurrencyToBackend(
          draft.orderingModuleConfig.deliveryModuleConfig.deliveryFee,
        ) || 0;

      // Convert coordinates to object format
      const { latitude, longitude } =
        draft.orderingModuleConfig.deliveryModuleConfig;
      if (latitude != null && longitude != null) {
        (
          draft.orderingModuleConfig
            .deliveryModuleConfig as IBackendDeliveryModuleConfig
        ).coordinates = {
          latitude,
          longitude,
        };
        delete (
          draft.orderingModuleConfig.deliveryModuleConfig as {
            latitude?: number;
            longitude?: number;
          }
        ).latitude;
        delete (
          draft.orderingModuleConfig.deliveryModuleConfig as {
            latitude?: number;
            longitude?: number;
          }
        ).longitude;
      }
    }

    // Convert prepTime to ISO duration format
    if (draft?.orderingModuleConfig) {
      draft.orderingModuleConfig.prepTime = convertPrepTimeToBackend(
        posProvider.orderingModuleConfig.prepTime,
      );

      if (posProvider.orderingModuleConfig.minLeadTime) {
        draft.orderingModuleConfig.minLeadTime = convertPrepTimeToBackend(
          posProvider.orderingModuleConfig.minLeadTime,
        );
      }

      if (posProvider.orderingModuleConfig.minTotalThreshold) {
        draft.orderingModuleConfig.minTotalThreshold =
          convertCurrencyToBackend(
            posProvider.orderingModuleConfig.minTotalThreshold,
          ) || 0;
      }

      if (posProvider.orderingModuleConfig.maxTotalThreshold) {
        draft.orderingModuleConfig.maxTotalThreshold =
          convertCurrencyToBackend(
            posProvider.orderingModuleConfig.maxTotalThreshold,
          ) || 0;
      }

      // Convert tax rate to decimal format if it exists
      if (
        "taxRate" in posProvider.orderingModuleConfig &&
        posProvider.orderingModuleConfig.taxRate !== undefined
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- grace - before eslint enforced
        (draft.orderingModuleConfig as any).taxRate = convertTaxRateToBackend(
          posProvider.orderingModuleConfig.taxRate as number,
        );
      }
    }

    // Convert menu config
    if (draft?.menuModuleConfig && posProvider.menuModuleConfig) {
      Object.assign(
        draft.menuModuleConfig,
        convertMenuConfigToBackend(posProvider.menuModuleConfig),
      );
    }

    // Handle Square locationId trimming
    if (posProvider.provider === "square") {
      (draft as IBackendPosProviderSquare).locationId =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- grace - before eslint enforced
        (posProvider as any).locationId?.trim() || undefined;
    }
  });
}
